// Pipeline health (CHANGE-070). What the loop says, what was asked of it, and how the app is set. An ask is only ever an ask
// until the loop acknowledges it after the ask, so nothing on this page says a stop happened before that.
import { useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { api } from '../../../lib'
import { Busy, Button, Err, Fresh, Page, Section, Sources, Table, ago, since, errorText, num, useKit, when, type Column } from '../kit'

type Confirm = 'never' | 'waiting' | 'confirmed' | 'differs'
type Switch = {
  name: string; label: string; desired: string; set_kind: string; set_by: string; set_at: string; reason: string
  actual: string | null; actual_at: string | null; actual_detail: string | null; confirm: Confirm
}
type Source = { id: string; label: string | null; read_at: string | null; limit_s: number; state: 'ok' | 'stale' | 'missing' | 'failed'; failed_at: string | null }
type Worker = { name: string; kind: string; code_sha: string | null; last_seen: string; limit_s: number }
type Alert = {
  id: string; class: string; title: string; body: string | null; default_if_ignored: string | null; diagnosis: string | null
  opened_at: string; actions: { label: string | null; to: string }[]
}
type Flag = { set: boolean; value: boolean; reason: string | null; at: string | null; by: string | null }
type Timer = {
  kind: string; spec: string; schedule: string; last_tick_at: string | null; tick_limit_s: number; last_due: string | null
  last_run_at: string | null; last_status: string | null; last_error: string | null
  state: 'ok' | 'never_ran' | 'missed' | 'failed' | 'no_tick'
}
type System = {
  at: string
  state: {
    sending: boolean | null; sent_today: number | null; goal: number | null; ready_next: number | null; next_send_day: string | null
    limiter: string | null; pulse_at: string | null; live: boolean; stopped: boolean; stop_confirmed: boolean
  }
  line: { title: string; sub: string | null }
  verdict: { answer: 'yes' | 'no' | 'maybe' | 'unknown'; title: string; sub: string; sentence: string; source: string; at: string | null }
  broken: { text: string; to: string }[]
  switches: Switch[]; sources: Source[]; workers: Worker[]; alerts: Alert[]; stuck: Alert[]; alerts_ever: number; alerts_cut: boolean
  app: {
    payouts_paused: Flag; card: Flag & { on: boolean; env: boolean; source: 'staff' | 'environment' }
    travel_enabled: boolean; payouts_mode: string | null; card_only_app: boolean
    hooks: { label: string; path: string; env: string; set: boolean }[]
  }
  timers: Timer[]
  start: { ok: boolean; why: string | null }
}

const KEY = ['staff', 'system']
const onOff = (v: boolean) => (v ? 'On' : 'Off')
const limitWords = (s: number) => (s < 3600 ? `${Math.round(s / 60)} min` : `${Math.round(s / 3600)} h`)
const JOB_LABEL: Record<string, string> = {
  'billing.month_close': 'Month-end bills', 'card.purge': 'Card request clean-up', 'crm.page_check': 'Page check', 'crm.sync': 'Records sync', 'inbox.check': 'Inbox check',
  'ledger.check_invariants': 'Ledger check', 'login.ploy_signups': 'Signups to Ploy', 'payout.sweep': 'Stuck payout sweep',
  'redeem.monthly': 'Monthly payback', 'stripe.poll': 'Stripe spend read', 'travel.purge': 'Trip data clean-up', 'email.pull': 'Partner mail read',
}
const NOT_REPORTING = "The pipeline server isn't reporting to this app yet"
const ServerFresh = ({ at, testid }: { at: string | null; testid: string }) => (at
  ? <Fresh at={at} label="Pipeline server" limit={600} testid={testid} />
  : <span className="st-fresh stale" data-testid={testid}>{NOT_REPORTING}</span>)
const SOURCE_WORD: Record<Source['state'], string> = { ok: 'Current', stale: 'Past its limit', missing: 'Not read', failed: 'Last run failed' }
const TIMER_WORD: Record<Timer['state'], string> = {
  ok: 'Ran when due', never_ran: 'No run yet', missed: 'No run at the last due time', failed: 'Last run failed', no_tick: 'Scheduler has not seen it',
}

// Words from the seed row and the switch's values, in plain English. Anything a person typed stays as typed.
const VALUE: Record<string, string> = { on: 'On', off: 'Off', mac: 'This Mac', engine: 'The engine' }
const SETUP_WHY: Record<string, string> = {
  'as found': 'Left as it was at setup', default: 'On by default', 'auto-ship starts off': 'Off until a person turns it on',
  'old path owns load': 'The Mac still loads leads',
}
const valueWord = (v: string | null) => (v ? VALUE[v] ?? v : '–')
const bySetup = (s: Switch) => s.set_by === 'migration'

function askWords(s: Switch): string {
  if (bySetup(s) && s.confirm !== 'differs' && s.confirm !== 'confirmed') return 'Nobody asked for a change'
  if (s.confirm === 'confirmed') return `Confirmed ${since(s.actual_at)}`
  if (s.confirm === 'differs') return `Asked for ${valueWord(s.desired).toLowerCase()}. The pipeline server says ${valueWord(s.actual).toLowerCase()}`
  return `Asked for ${valueWord(s.desired).toLowerCase()} ${since(s.set_at)}. Nothing has confirmed it`
}

/** The real stop while the loop reads nothing. A link only: this page never calls Instantly. */
const StopNow = () => (
  <span data-testid="system-stop-now">To stop now, pause campaigns in <a className="st-link" href="https://app.instantly.ai" target="_blank" rel="noreferrer" data-testid="system-instantly">Instantly</a></span>
)

/** A detail that stays folded until asked for. A link to its #id opens it. */
function Fold({ id, title, note, bad, children, testid }: { id: string; title: string; note?: ReactNode; bad?: boolean; children: ReactNode; testid?: string }) {
  const { hash } = useLocation()
  const [open, setOpen] = useState(hash === `#${id}`)
  useEffect(() => { if (hash === `#${id}`) setOpen(true) }, [hash, id])
  return (
    <details id={id} className="st-sys-fold" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)} data-testid={testid}>
      <summary data-testid={testid ? `${testid}-fold` : undefined}>{title}{note ? <span className={bad ? 'bad' : undefined}>{note}</span> : null}</summary>
      {open ? <div className="st-sys-fold-body">{children}</div> : null}
    </details>
  )
}

const whoSet = (s: Switch): string =>
  s.set_kind !== 'person' ? `The ${s.set_kind}` : s.set_by === 'migration' ? 'Setup' : s.set_by

const paidBy = (by: string | null): string => (by === 'system' ? 'the nightly ledger check' : by ?? 'something not in the audit log')

export function SystemPage() {
  const qc = useQueryClient()
  const kit = useKit()
  const { hash } = useLocation()
  const q = useQuery({ queryKey: KEY, queryFn: () => api<System>('GET', '/api/staff/system'), refetchInterval: 15_000 })
  const d = q.data
  useEffect(() => {
    if (!d || !hash) return
    const t = requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView())
    return () => cancelAnimationFrame(t)
  }, [hash, !d])     // eslint-disable-line react-hooks/exhaustive-deps

  const ask = useMutation({
    mutationFn: (v: { to: 'stop' | 'start'; reason: string; key: string }) =>
      api<{ changed: boolean }>('POST', `/api/staff/system/${v.to}`, { reason: v.reason, idempotency_key: v.key }),
    onSuccess: (r, v) => {
      qc.invalidateQueries({ queryKey: KEY })
      kit.toast(!r.changed ? 'Already asked. Nothing changed' : v.to === 'stop' ? 'Stop asked. Nothing has confirmed it' : 'Start asked. Nothing has confirmed it')
    },
    onError: (e) => kit.toast(errorText(e), { bad: true }),
  })
  const send = d?.switches.find((s) => s.name === 'send')
  const unread = send?.confirm === 'never'
  const asked = d?.state.stopped ?? false

  const stop = async () => {
    const r = await kit.confirm({
      title: 'Ask to stop',
      body: unread ? <>This records the ask. The pipeline server does not read this switch yet, so nothing stops until it does. <StopNow /></> : 'The pipeline server stops at its next check.',
      confirm: 'Ask to stop', danger: true, reason: { label: 'Why', required: true },
    })
    if (r.ok && r.reason) ask.mutate({ to: 'stop', reason: r.reason, key: crypto.randomUUID() })
  }
  const start = async () => {
    const r = await kit.confirm({
      title: 'Ask to start',
      body: unread ? 'This records the ask. The pipeline server does not read this switch yet, so nothing changes until it does.' : 'The pipeline server starts at its next check.',
      confirm: 'Ask to start', reason: { label: 'Why', required: true },
    })
    if (r.ok && r.reason) ask.mutate({ to: 'start', reason: r.reason, key: crypto.randomUUID() })
  }
  // ⌘K's Stop sending lands here and opens the same confirm.
  const [params, setParams] = useSearchParams()
  useEffect(() => {
    if (params.get('stop') !== '1' || !d) return
    setParams({}, { replace: true })
    if (!asked) void stop()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, d])

  if (q.isLoading) return <Page title="Pipeline health" testid="system-page"><Busy /></Page>
  if (!d) return <Page title="Pipeline health" testid="system-page"><Err error={q.error} /></Page>
  const s = d.state

  const control = asked ? (
    <span className="st-row">
      {d.start.ok ? null : <span className="st-note" data-testid="system-start-why">{d.start.why}</span>}
      <Button disabled={!d.start.ok || ask.isPending} onClick={start} data-testid="system-start">Ask to start</Button>
    </span>
  ) : (
    <span className="st-stop">
      <Button disabled={ask.isPending} onClick={stop} data-testid="system-stop">Ask to stop</Button>
      {unread ? <StopNow /> : null}
    </span>
  )
  const asks = d.switches.filter((w) => !bySetup(w))
  const old = d.sources.filter((r) => r.state !== 'ok' && r.id !== 'pulse').length
  const late = d.timers.filter((t) => t.state === 'failed' || t.state === 'missed' || t.state === 'no_tick').length
  const loopHook = d.app.hooks.find((h) => h.label === 'Pipeline server')
  const quiet = !d.alerts.length && !d.stuck.length

  return (
    <Page title="Pipeline health" testid="system-page" right={control}>
      <section className="st-verdict" data-testid="system-verdict" data-answer={d.verdict.answer}>
        <p className={`st-verdict-title${d.verdict.answer === 'unknown' || d.verdict.answer === 'maybe' ? ' unknown' : ''}`} data-testid="system-line">{d.verdict.title}</p>
        <p className="st-note" data-testid="system-verdict-sub">{d.verdict.sub}</p>
        <ServerFresh at={s.pulse_at} testid="system-read" />
      </section>
      <p className="st-broken" data-testid="system-broken">
        {d.broken.length ? d.broken.map((b) => <Link key={b.text} className="st-link st-bad" to={b.to}>{b.text}</Link>)
          : <span className="st-muted">Nothing broken in what was read</span>}
      </p>
      {q.error ? <Err error={q.error} /> : null}

      {s.live ? (
        <dl className="st-grid st-sys-state" data-testid="system-state">
          <div><dt>Sent today</dt><dd>{num(s.sent_today)}</dd></div>
          <div><dt>Goal</dt><dd>{num(s.goal)}</dd></div>
          <div><dt>Ready</dt><dd>{num(s.ready_next)}</dd></div>
          <div><dt>Limit</dt><dd>{s.limiter ?? '–'}</dd></div>
        </dl>
      ) : null}

      {quiet ? (
        <p className="st-note" data-testid="system-alerts-none">{d.alerts_ever ? 'No alerts open' : 'Nothing has written an alert yet'}</p>
      ) : (
        <>
          <Section id="alerts" title="Alerts" count={d.alerts.length} testid="system-alerts">
            <AlertTable rows={d.alerts} ever={d.alerts_ever} cut={d.alerts_cut} />
          </Section>
          {d.stuck.length ? (
            <Section id="stuck" title="Stuck" count={d.stuck.length} testid="system-stuck">
              <AlertTable rows={d.stuck} ever={d.alerts_ever} cut={false} />
            </Section>
          ) : null}
        </>
      )}

      <div className="st-sys-folds">
        <Fold id="switches" title="Switches" testid="system-switches" note={asks.length ? `${asks.length} asked` : 'Nobody asked for a change'}>
          {unread ? <p className="st-note" data-testid="system-unread">The pipeline server does not read this switch yet. Ask to stop and Ask to start record the ask and change nothing until it does. <StopNow /></p> : null}
          <Table testid="system-switch-table" rows={d.switches} rowKey={(r) => r.name} columns={switchColumns} />
          <Link className="st-link st-fresh" to="/staff/log?object_type=growth_switch">Who asked what</Link>
        </Fold>
        <Fold id="sources" title="Sources" testid="system-sources" bad={old > 0} note={old ? `${old} old` : 'All current'}>
          <Sources>
            <Fresh at={d.at} live label="Page" limit={60} testid="system-fresh" />
            <ServerFresh at={s.pulse_at} testid="system-pulse" />
          </Sources>
          <Table testid="system-source-table" rows={d.sources} rowKey={(r) => r.id} columns={sourceColumns} />
        </Fold>
        <Fold id="workers" title="Workers" testid="system-workers" note={d.workers.length ? `${d.workers.length} reporting` : 'None has reported'}>
          <Table rows={d.workers} rowKey={(r) => r.name} columns={workerColumns} empty="No worker has reported" />
        </Fold>
        <Fold id="app" title="App" testid="system-app" bad={d.app.payouts_paused.value} note={d.app.payouts_paused.value ? 'Payouts paused' : loopHook && !loopHook.set ? NOT_REPORTING : 'Payouts not paused'}>
          <Fresh at={d.at} live limit={60} />
          <AppFacts app={d.app} />
          <Table testid="system-hooks" rows={d.app.hooks} rowKey={(r) => r.env} columns={hookColumns} />
        </Fold>
        <Fold id="timers" title="Timers" testid="system-timers" bad={late > 0} note={late ? `${late} late` : 'None late'}>
          <Table testid="system-timer-table" rows={d.timers} rowKey={(r) => r.kind} columns={timerColumns} />
        </Fold>
      </div>
    </Page>
  )
}

const switchColumns: Column<Switch>[] = [
  { key: 'name', head: 'Switch', primary: true, cell: (r) => <span title={r.name}>{r.label}</span> },
  { key: 'state', head: 'Ask', cell: (r) => (
    <span data-testid={`system-switch-${r.name}`} className={r.confirm === 'differs' ? 'st-bad' : r.confirm !== 'confirmed' && !bySetup(r) ? 'st-warn' : undefined}>{askWords(r)}</span>
  ) },
  { key: 'actual', head: 'Server says', cell: (r) => (r.actual_at ? <span title={r.actual_detail ?? undefined}>{valueWord(r.actual)}</span> : <span className="st-muted">Not read yet</span>) },
  { key: 'ack', head: 'Last acknowledged', nowrap: true, cell: (r) => <span title={when(r.actual_at)}>{r.actual_at ? `${since(r.actual_at)}` : '–'}</span> },
  { key: 'who', head: 'Who', cell: whoSet },
  { key: 'why', head: 'Why', cell: (r) => (bySetup(r) ? SETUP_WHY[r.reason] ?? r.reason : r.reason) },
]

const sourceColumns: Column<Source>[] = [
  { key: 'label', head: 'Source', primary: true, cell: (r) => r.label ?? r.id },
  { key: 'read', head: 'Read', nowrap: true, cell: (r) => (r.id === 'pulse' && !r.read_at ? '–' : <Fresh at={r.read_at} limit={r.limit_s} />) },
  { key: 'limit', head: 'Stale after', nowrap: true, cell: (r) => limitWords(r.limit_s) },
  { key: 'state', head: 'State', cell: (r) => <span className={r.state === 'ok' ? undefined : 'st-bad'}>{r.id === 'pulse' && r.state === 'missing' ? NOT_REPORTING : SOURCE_WORD[r.state]}</span> },
  { key: 'failed', head: 'Last failure', nowrap: true, cell: (r) => (r.failed_at ? when(r.failed_at) : <span className="st-muted">None kept</span>) },
]

const workerColumns: Column<Worker>[] = [
  { key: 'name', head: 'Worker', primary: true, cell: (r) => r.name },
  { key: 'kind', head: 'Kind', cell: (r) => r.kind },
  { key: 'seen', head: 'Last seen', nowrap: true, cell: (r) => <Fresh at={r.last_seen} limit={r.limit_s} /> },
  { key: 'sha', head: 'Code', cell: (r) => r.code_sha },
]

const hookColumns: Column<System['app']['hooks'][number]>[] = [
  { key: 'label', head: 'Hook', primary: true, cell: (r) => <span title={r.path}>{r.label}</span> },
  { key: 'set', head: 'State', cell: (r) => (r.set ? 'On' : <span className="st-muted">Off</span>) },
]

const timerColumns: Column<Timer>[] = [
  { key: 'kind', head: 'Job', primary: true, cell: (r) => <span title={r.kind}>{JOB_LABEL[r.kind] ?? r.kind}</span> },
  { key: 'schedule', head: 'Schedule', cell: (r) => <span title={r.spec}>{r.schedule}</span> },
  { key: 'tick', head: 'Scheduler checked', nowrap: true, cell: (r) => <Fresh at={r.last_tick_at} limit={r.tick_limit_s} /> },
  { key: 'due', head: 'Last due', nowrap: true, cell: (r) => when(r.last_due) },
  { key: 'run', head: 'Last run', nowrap: true, cell: (r) => (r.last_run_at ? `${when(r.last_run_at)}, ${r.last_status}` : '–') },
  { key: 'state', head: 'State', cell: (r) => <span className={r.state === 'ok' || r.state === 'never_ran' ? undefined : 'st-bad'} title={r.last_error ?? undefined}>{TIMER_WORD[r.state]}</span> },
]

function AppFacts({ app }: { app: System['app'] }) {
  const p = app.payouts_paused
  const mode = app.payouts_mode === 'manual' ? 'Manual, staff send by hand' : app.payouts_mode === 'stripe' ? 'Stripe' : 'Not read'
  return (
    <dl className="st-facts">
      <dt>Payouts</dt>
      <dd data-testid="system-payouts">{p.value
        ? <><span className="st-bad st-strong">Paused</span> by {paidBy(p.by)}, {when(p.at)}{p.reason ? `. ${p.reason}` : ''}. Nothing in the app clears this</>
        : 'Not paused'}</dd>
      <dt>Card requests</dt>
      <dd data-testid="system-card">{onOff(app.card.on)}, {app.card.source === 'staff' ? `staff switch, set by ${app.card.by ?? 'not recorded'} ${when(app.card.at)}` : 'from the deploy setting'}. <Link className="st-link" to="/staff/card">Card requests</Link></dd>
      <dt>Travel</dt><dd>{onOff(app.travel_enabled)}</dd>
      <dt>Payouts mode</dt><dd data-testid="system-mode">{mode}</dd>
      <dt>Card-only app</dt><dd>{onOff(app.card_only_app)}</dd>
    </dl>
  )
}

function AlertTable({ rows, ever, cut }: { rows: Alert[]; ever: number; cut: boolean }) {
  const qc = useQueryClient()
  const kit = useKit()
  const resolve = useMutation({
    mutationFn: (v: { id: string; note: string }) => api('POST', `/api/staff/system/alerts/${v.id}/resolve`, { note: v.note }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: KEY }); kit.toast('Resolved') },
    onError: (e) => { qc.invalidateQueries({ queryKey: KEY }); kit.toast(errorText(e), { bad: true }) },
  })
  const open = async (a: Alert) => {
    const r = await kit.confirm({ title: 'Resolve alert', body: a.title, confirm: 'Resolve', reason: { label: 'What was done', required: true } })
    if (r.ok && r.reason) resolve.mutate({ id: a.id, note: r.reason })
  }
  const columns: Column<Alert>[] = [
    { key: 'title', head: 'Alert', primary: true, cell: (a) => (
      <div>{a.title}{a.body ? <div className="st-tl-sub">{a.body}</div> : null}{a.diagnosis ? <div className="st-tl-sub">{a.diagnosis}</div> : null}</div>
    ) },
    { key: 'ignored', head: 'If ignored', cell: (a) => a.default_if_ignored },
    { key: 'opened', head: 'Opened', nowrap: true, cell: (a) => <span title={when(a.opened_at)}>{since(a.opened_at)}</span> },
    { key: 'do', head: '', label: 'Do', cell: (a) => (
      <span className="st-row">
        {a.actions.map((x) => <Link key={x.to} className="st-link" to={x.to}>{x.label ?? 'Open'}</Link>)}
        <Button tone="quiet" disabled={resolve.isPending && resolve.variables?.id === a.id} onClick={() => open(a)} data-testid="system-resolve">Resolve</Button>
      </span>
    ) },
  ]
  return (
    <>
      <Table rows={rows} rowKey={(a) => a.id} columns={columns} empty={ever ? 'None open' : 'Nothing has written an alert yet'} />
      {cut ? <p className="st-note">Showing the newest 100</p> : null}
    </>
  )
}
